import {
  useBrowseProductCardsApi,
  useBrowseProductEntitlementApi,
} from '@aibees/shared/api';
import {
  PlanSlug,
  type FeatureCatalog,
  type Plan,
  type ProductCards,
  type Subscription,
} from '@aibees/shared/definition';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import {
  buildFeatureGroups,
  matchColumnKey,
} from '@aibees/shared/features/price-and-plan/components/pricing-cards/catalog';
import { fDate, fNumber } from '@aibees/shared/utils';
import { useMemo } from 'react';
import { CurrentPlanVariant, type CurrentPlanData } from '../types';

const FALLBACK_PLAN_SLUG = PlanSlug.SCALE;
const DASH = '—';
const SUPPORT_CODES = new Set(['SUPPORT_CHAT', 'SUPPORT_PRIVATE_SLACK']);

function isLegacyPlan(plan: Plan): boolean {
  return (
    plan.product?.metadata?.is_legacy === 'true' ||
    plan.metadata?.is_legacy === 'true'
  );
}

function isNonPublicPlan(plan: Plan): boolean {
  const resolved =
    plan.product?.metadata?.is_public ?? plan.metadata?.is_public;
  return resolved !== 'true';
}

function resolvePlanVariant(plan: Plan | undefined): CurrentPlanVariant | null {
  if (!plan) return null;
  if (isLegacyPlan(plan)) return CurrentPlanVariant.LEGACY;
  if (isNonPublicPlan(plan)) return CurrentPlanVariant.CUSTOM;
  return null;
}

function formatCredits(request: string | undefined): string {
  const value = Number(request);
  if (!value) return DASH;
  if (value >= 1000) return `${fNumber(value / 1000)}K / Month`;
  return `${fNumber(value)} / Month`;
}

function formatSeats(seat: string | null | undefined): string {
  const value = Number(seat);
  if (!value) return DASH;
  return `${value} Users`;
}

function formatRollover(
  rollover: string | undefined,
  request: string | undefined
): string {
  const rolloverValue = Number(rollover);
  const requestValue = Number(request);
  if (!rolloverValue || !requestValue) return DASH;
  const ratio = rolloverValue / requestValue;
  return `x${Number.isInteger(ratio) ? ratio : ratio.toFixed(1)}`;
}

function formatRate(dollars: number, request: string | undefined): string {
  const credits = Number(request);
  if (!dollars || !credits) return DASH;
  const perThousand = dollars / (credits / 1000);
  return `$${perThousand.toFixed(2)} / 1K Credits`;
}

function resolveSupport(
  catalog: FeatureCatalog | undefined,
  productId: string | undefined
): string {
  if (!catalog || !productId) return DASH;
  const product = catalog.products.find((p) => p.id === productId);
  if (!product) return DASH;
  const support = catalog.features.find((f) =>
    SUPPORT_CODES.has(f.metadata.code ?? '')
  );
  if (!support) return DASH;
  // Column keys inside `cells` don't reliably match the product metadata's
  // casing (or its family-vs-name choice), so match against the keys actually
  // present rather than looking up `family ?? name` directly.
  const columnKey = matchColumnKey(product, Object.keys(support.cells ?? {}));
  const cell = columnKey ? support.cells?.[columnKey] : undefined;
  // The catalog carries per-column values under `valuesPerPlan`; plain `value`
  // is the flat-cell fallback (same precedence as the compare table and cards).
  const value = cell?.valuesPerPlan?.value ?? cell?.value;
  if (value) {
    return value.replace(' Channel', '');
  }
  return DASH;
}

function resolveFeatureGroups(
  cards: ProductCards | undefined,
  productId: string | undefined
) {
  if (!cards) return [];
  if (productId && cards.products.some((p) => p.id === productId)) {
    return buildFeatureGroups(productId, cards);
  }
  const fallbackId = cards.products.find(
    (p) => p.metadata.slug === FALLBACK_PLAN_SLUG
  )?.id;
  if (!fallbackId) return [];
  return buildFeatureGroups(fallbackId, cards);
}

function deriveName(
  subscription: Subscription | undefined,
  variant: CurrentPlanVariant
): string {
  return (
    subscription?.current?.plan?.product?.name ??
    (variant === CurrentPlanVariant.LEGACY ? 'Legacy' : 'Custom')
  );
}

export function useCurrentPlan(): {
  data: CurrentPlanData | null;
  isLoading: boolean;
} {
  const { data: subscription, isLoading: subLoading } =
    useCurrentSubscriptionApi();
  const { data: catalog, isLoading: catalogLoading } =
    useBrowseProductEntitlementApi();
  const { data: cards, isLoading: cardsLoading } = useBrowseProductCardsApi();

  const isLoading = subLoading || catalogLoading || cardsLoading;

  return useMemo(() => {
    const current = subscription?.current;
    const plan = current?.plan;
    const variant = resolvePlanVariant(plan);
    if (!plan || !variant) return { data: null, isLoading };

    const productId = plan.product?.id;
    const meta = plan.metadata;
    // Plan / subscription amounts are normalised to whole dollars by the API
    // (see PlanPrice + useCreditRate, which both treat `amount` as dollars).
    const price = current?.amount ?? plan.amount ?? 0;

    const data: CurrentPlanData = {
      variant,
      name: deriveName(subscription, variant),
      price,
      monthLabel: '/ Month',
      rate: formatRate(price, meta?.request),
      credits: formatCredits(meta?.request),
      requestCount: Number(meta?.request) || 0,
      seats: formatSeats(meta?.seat),
      rollover: formatRollover(meta?.rollover, meta?.request),
      support: resolveSupport(catalog, productId),
      renewal: current?.currentPeriodEnd
        ? fDate(current.currentPeriodEnd, 'MMM d, yyyy')
        : DASH,
      featureGroups: resolveFeatureGroups(cards, productId),
    };

    return { data, isLoading };
  }, [subscription, catalog, cards, isLoading]);
}
